// SectionNavigation - Navegación entre secciones con indicadores de progreso
// Arquitectura actualizada: Sections (navegación) + Categories (análisis)

import React from 'react';
import { Box, Tabs, Tab, Chip } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { QuestionResponse } from '../../../types/audit.types';

interface Section {
  id: number;
  nombre: string;
  descripcion?: string;
  orden: number;
  color?: string;
  questions?: any[];
}

interface SectionNavigationProps {
  sections: Section[];
  seccionActual: number;
  onChange: (seccionId: number) => void;
  respuestas: Map<number, QuestionResponse>;
}

const SectionNavigation: React.FC<SectionNavigationProps> = ({
  sections,
  seccionActual,
  onChange,
  respuestas,
}) => {

  // Calcular progreso por sección
  const getSectionProgress = (section: Section) => {
    const questions = section.questions || [];
    const totalQuestions = questions.length;
    const questionsAnswered = questions.filter((q: any) =>
      respuestas.has(q.id)
    ).length;
    const porcentaje = totalQuestions > 0 
      ? Math.round((questionsAnswered / totalQuestions) * 100)
      : 0;
    const completa = questionsAnswered === totalQuestions && totalQuestions > 0;

    return { questionsAnswered, totalQuestions, porcentaje, completa };
  };

  // Encontrar el índice de la sección actual
  const currentTabIndex = sections.findIndex(s => s.id === seccionActual);

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: 1,
      }}
    >
      <Tabs
        value={currentTabIndex !== -1 ? currentTabIndex : 0}
        onChange={(_, newIndex) => onChange(sections[newIndex].id)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: { xs: 48, sm: 56 },
          '& .MuiTab-root': {
            minHeight: { xs: 48, sm: 56 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 500,
            textTransform: 'none',
            px: { xs: 1.5, sm: 2 },
          },
        }}
      >
        {sections.map((section) => {
          const progress = getSectionProgress(section);
          
          return (
            <Tab
              key={section.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Indicador de color de la sección */}
                  {section.color && (
                    <Box
                      component="span"
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: section.color,
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  
                  {/* Nombre de la sección */}
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: { xs: 100, sm: 150 },
                    }}
                  >
                    {section.nombre}
                  </Box>

                  {/* Badge con progreso */}
                  <Chip
                    label={`${progress.questionsAnswered}/${progress.totalQuestions}`}
                    size="small"
                    color={progress.completa ? 'success' : 'default'}
                    icon={progress.completa ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
                    sx={{
                      height: { xs: 20, sm: 22 },
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                      '& .MuiChip-icon': {
                        ml: 0.5,
                        mr: -0.25,
                      },
                    }}
                  />
                </Box>
              }
              sx={{
                opacity: 1,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default SectionNavigation;
